import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWizard } from '../../context/WizardContext';
import WizardProgress from '../../components/WizardProgress';
import PDFUpload from '../../components/PDFUpload';
import { listPDFTemplates, PDFTemplate } from '../../api/client';
import '../MappingWizard.css';

const Step2Template: React.FC = () => {
  const navigate = useNavigate();
  const { state, saveDraft } = useWizard();

  const [templates, setTemplates] = useState<PDFTemplate[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(state.pdfTemplateId);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await listPDFTemplates();
      setTemplates(data);
    } catch (err) {
      // Error silently handled
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = (template: PDFTemplate) => {
    setTemplates([...templates, template]);
    setSelectedId(template.id);
  };

  const handleNext = async () => {
    if (!selectedId) {
      alert('Bitte wähle ein PDF-Formular aus');
      return;
    }

    setSaving(true);
    try {
      await saveDraft(2, { pdfTemplateId: selectedId });
      navigate('/wizard/datasources');
    } catch (err: any) {
      alert(`Fehler beim Speichern: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      await saveDraft(2, { pdfTemplateId: selectedId });
      alert('Entwurf gespeichert!');
    } catch (err: any) {
      alert(`Fehler beim Speichern: ${err.response?.data?.error || err.message}`);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleBack = () => {
    navigate('/wizard/project');
  };

  return (
    <>
      <WizardProgress currentStep={2} totalSteps={5} />
      <div className="wizard-step">
        <h2>PDF-Formular auswählen</h2>
        <p className="step-description">
          Lade ein neues PDF-Formular hoch oder wähle ein bereits hochgeladenes Formular.
        </p>

        {/* Upload Area */}
        <div className="upload-section">
          <h3>Neues PDF hochladen</h3>
          <PDFUpload onUploadSuccess={handleUploadSuccess} />
        </div>

        {/* Template Selection */}
        <div className="selection-section">
          <h3>Oder bestehendes Formular wählen</h3>
          {loading ? (
            <p>Lade Formulare...</p>
          ) : templates.length === 0 ? (
            <p className="no-data">Keine Formulare vorhanden. Bitte lade ein PDF hoch.</p>
          ) : (
            <div className="template-grid">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className={`template-card ${selectedId === template.id ? 'selected' : ''}`}
                >
                  <div className="template-icon">📄</div>
                  <div className="template-info">
                    <h4>{template.name}</h4>
                    <p>{template.fields.length} Felder</p>
                    <small>{new Date(template.created_at).toLocaleDateString()}</small>
                  </div>
                  <div className="card-actions">
                    <button
                      className="btn-select"
                      onClick={() => setSelectedId(template.id)}
                    >
                      {selectedId === template.id ? '✓ Ausgewählt' : 'Auswählen'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="wizard-actions">
          <button onClick={handleBack} className="btn-secondary">
            ← Zurück
          </button>
          <button
            onClick={handleSaveDraft}
            className="btn-secondary"
            disabled={savingDraft || saving}
            style={{ marginLeft: 'auto' }}
          >
            {savingDraft ? 'Speichere...' : 'Entwurf speichern'}
          </button>
          <button
            onClick={handleNext}
            className="btn-primary"
            disabled={!selectedId || saving || savingDraft}
          >
            {saving ? 'Speichere...' : 'Weiter →'}
          </button>
        </div>
      </div>
    </>
  );
};

export default Step2Template;
